import gql from 'graphql-tag';

/**
 * Query the latest state of an inspection template directly from event aggregates.
 *
 * Unlike `inspectionTemplate`, this bypasses the projection table and always
 * reflects the latest event stream — useful immediately after writes (e.g. a
 * fork) where the projection may not have caught up yet.
 *
 * Returns a `CurrentInspectionTemplateResult` wrapper containing both the
 * requested template and (optionally) the parent channel blueprint it was
 * forked from. Keeping `baselineTemplate` on the wrapper instead of the
 * core `InspectionTemplate` type avoids polluting that type with a field
 * only meaningful in the admin edit flow.
 */
export const currentInspectionTemplateQuery = gql`
  fragment InspectionTemplateFragment on InspectionTemplate {
    id
    schemaVersion
    version
    parentTemplateId
    locked
    parentTemplateLockedVersion
    metadata {
      name
      description
      templateType
      assetTypes
      tags
      buildingId {
        sourceEntityId
        sourceEntityType
        sourceType
      }
      createdAt
      createdBy
      updatedAt
      updatedBy
    }
    config {
      requirePhotoOnDeficiency
      maxPhotosPerItem
      requireNoteOnDeficiency
      defaultQuestionType
      allowObservationItems
      completionRules {
        allItemsRequired
        allowPartialSubmission
      }
    }
    sections {
      id
      name
      description
      order
      config {
        requirePhotoOnDeficiency
        skippable
        collapsedByDefault
      }
      items {
        id
        label
        helpText
        order
        questionType
        questionConfig
        required
        photosEnabled
        notesEnabled
        config {
          requirePhotoOnDeficiency
          requireNoteOnDeficiency
          minPhotos
          isComplianceItem
          tags
          category
        }
      }
    }
  }

  query currentInspectionTemplate(
    $id: UUID!
    $channelId: UUID!
    $version: Int
  ) {
    currentInspectionTemplate(
      id: $id
      channelId: $channelId
      version: $version
    ) {
      template {
        ...InspectionTemplateFragment
      }
      baselineTemplate {
        ...InspectionTemplateFragment
      }
    }
  }
`;
